import { Alert, Container, Pagination, Snackbar, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  checkUploadedFile,
  listAllProducts,
  listProductsbyCategory,
} from '../../api/products';
import DashboardLayout from '../../layout/dashboard/DashboardLayout';
import { autosuggestFields } from '../../staticData/tableHead';
import useAuthToken from '../../hooks/getUserData';
import ProductListTable from '../../components/autosuggest-products/ProductListTable';
import AddProduct from '../../components/autosuggest-products/AddProduct';

const AutosuggestionProducts = () => {
  const token = useAuthToken();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [page, setPage] = useState(0);
  const pageSize = 15;
  const [totalPages, setTotalPages] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [alreadyUploading, setAlreadyUploading] = useState(false);

  const fetchData = async (pageNumber) => {
    try {
      if (token) {
        setLoading(true);
        const pageToFetch = pageNumber !== undefined ? pageNumber : page;
        let response;
        const addedBy = 'suggestion';
        if (selectedCategory !== 'All Products') {
          response = await listProductsbyCategory(
            pageToFetch,
            pageSize,
            selectedCategory,
            token,
            addedBy
          );
        } else {
          response = await listAllProducts(
            pageToFetch,
            pageSize,
            token,
            filterName,
            addedBy
          );
        }

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const data = await response.json();
        setItems(data?.products);
        setError(null);

        setTotalPages(Math.ceil(data?.count / pageSize));
      }
    } catch (error) {
      setError(error.message || 'An error occurred while fetching products');
    } finally {
      setLoading(false);
    }
  };

  const checkIfUploading = async () => {
    setAlreadyUploading(false);
    try {
      const response = await checkUploadedFile(token);
      const data = await response.json();
      if (data.success === true) {
        setAlreadyUploading(true);
      }
    } catch {
      // console.log('uploading');
    }
  };

  useEffect(() => {
    if (token) {
      fetchData(0);
      setPage(0);
      checkIfUploading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, token, filterName]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage + 1);
  };

  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    setFilterName('');
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setSelectedCategory('All Products');
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Stack spacing={2}>
          <>
            {error ? (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : (
              <>
                <Snackbar
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  open={snackbarOpen}
                  autoHideDuration={3000}
                  onClose={() => setSnackbarOpen(false)}>
                  <Alert severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                  </Alert>
                </Snackbar>
                {alreadyUploading && (
                  <Alert severity="info">
                    Product creation is currently in progress. Please refresh
                    the page to see the updated list.
                  </Alert>
                )}
                <ProductListTable
                  filteredData={items}
                  columnFields={autosuggestFields}
                  tableName={'Auto suggestion products'}
                  addModal={
                    <AddProduct
                      fetchData={fetchData}
                      token={token}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                      alreadyUploading={alreadyUploading}
                    />
                  }
                  loading={loading}
                  setError={setError}
                  selectedCategory={selectedCategory}
                  handleCategoryChange={handleCategoryChange}
                  handleFilterByName={handleFilterByName}
                  filterName={filterName}
                  fetchData={fetchData}
                  page={page}
                  pageSize={pageSize}
                  token={token}
                  setPage={setPage}
                  setSelectedCategory={setSelectedCategory}
                  setMessage={setSnackbarMessage}
                  setSnackbarOpen={setSnackbarOpen}
                />
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(event, newPage) => handlePageChange(newPage - 1)}
                    sx={{ alignSelf: 'center' }}
                  />
                )}
              </>
            )}
          </>
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default AutosuggestionProducts;
